/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: types/pinLists.types.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    TypeScript type definitions for PIN Lists module.
    Centralized types for better type safety and maintainability.
────────────────────────────────────────────────────────────────*/

/**
 * PIN List Member
 */
export interface PinMember {
  id?: number;
  tempId: string;
  pin: string;
  description: string;
  enabled: boolean;
}

/**
 * PIN List from API
 */
export interface PinList {
  id: number;
  uuid: string;
  domainId: number;
  name: string;
  description?: string | null;
  enabled: boolean;
  createdAt: string;
  createdBy?: number | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  members?: Array<{
    id: number;
    pin: string;
    description: string | null;
    enabled: boolean;
  }>;
  _count?: {
    members: number;
  };
}

/**
 * PIN List API Response
 */
export interface PinListResponse {
  name: string;
  description: string | null;
  enabled: boolean;
  members?: Array<{
    id: number;
    pin: string;
    description: string | null;
    enabled: boolean;
  }>;
}

/**
 * Form state for PIN List
 */
export interface FormState {
  name: string;
  description: string;
  enabled: boolean;
}

/**
 * Validation errors
 */
export interface ValidationErrors {
  [key: string]: string;
}

/**
 * List query parameters
 */
export interface ListPinListsParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

/**
 * List response with pagination
 */
export interface ListPinListsResponse {
  success: boolean;
  data: PinList[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Create PIN List payload
 */
export interface CreatePinListPayload {
  name: string;
  description: string | null;
  enabled: boolean;
  members: Array<{
    pin: string;
    description: string | null;
    enabled: boolean;
  }>;
}

/**
 * Update PIN List payload
 */
export interface UpdatePinListPayload {
  name?: string;
  description?: string | null;
  enabled?: boolean;
  members?: Array<{
    pin: string;
    description: string | null;
    enabled: boolean;
  }>;
}

/**
 * PIN regex pattern
 */
export const PIN_REGEX = /^[*#0-9A-Za-z_-]{1,16}$/;

